import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import StickyFooter, {
  STICKY_FOOTER_CONTAINER_STYLES,
  STICKY_FOOTER_CONTENT_STYLES,
} from 'molbio/proteins/alphafold/structure-prediction/StickyFooter';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import PreviewBadge from 'ui-library/badges/PreviewBadge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import NumericInput from 'ui-library/inputs/NumericInput';
import {assertNever, NumericInputChangeEvent} from 'util/Types';

import {BoltzGenTargetType} from './GenerativeModelsConstants';
import {runBoltzGen} from './GenerativeModelsDAO';

interface GenerativeModelFormProps {
  currentEntityType: BoltzGenTargetType;
  targetId: string;
  navigateToList: () => void;
}

function getEntityTypeDisplayName(currentEntityType: BoltzGenTargetType): string {
  switch (currentEntityType) {
    case ModelTypeConstants.PROTEIN:
      return 'AA sequence';
    case ModelTypeConstants.MOLECULE:
      return 'Molecule';
    default:
      return assertNever(currentEntityType);
  }
}

export default function GenerativeModelForm({
  currentEntityType,
  targetId,
  navigateToList,
}: GenerativeModelFormProps): JSX.Element {
  const [[rangeMin, rangeMax], setRange] = useState<[number, number]>([20, 50]);

  return (
    <div className={css(styles.outerContainer)}>
      <div className={css(styles.content)}>
        <div className={css(styles.contentPadding)}>
          <div className={css(styles.backButtonContainer)}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              icon="chevron-left"
              text="View all runs"
              onClick={navigateToList}
            />
          </div>
          <div className={css(styles.titleContainer)}>
            <div className={css(styles.title)}>BoltzGen</div>
            <PreviewBadge />
          </div>
          <p className={css(styles.description)}>
            Using the current {getEntityTypeDisplayName(currentEntityType)} as a target, generate new AA
            sequences that bind to this target. We'll show you the top-ranked result, and you'll be able to
            download the rest for further analysis. For more information about BoltzGen, see{' '}
            <a href="https://boltz.bio/boltzgen" target="_blank" rel="noopener noreferrer">
              here
            </a>
            .
          </p>
          <p className={css(styles.description)}>
            Choose how large you want the generated sequences to be. BoltzGen will randomly generate sequences
            between the min and max size.
          </p>
          <div className={css(styles.numericInputContainer)}>
            <div className={css(styles.numericInputRow)}>
              <div className={css(styles.label)}>Minimum Sequence Length</div>
              <NumericInput
                className={css(styles.numericInput)}
                min={1}
                max={1000}
                value={rangeMin}
                onChange={(e: NumericInputChangeEvent) => {
                  if (e.target.numericValue == null) {
                    return;
                  }
                  setRange([e.target.numericValue, rangeMax]);
                }}
              />
            </div>
            <div className={css(styles.numericInputRow)}>
              <div className={css(styles.label)}>Maximum Sequence Length</div>
              <NumericInput
                className={css(styles.numericInput)}
                min={1}
                max={1000}
                value={rangeMax}
                onChange={(e: NumericInputChangeEvent) => {
                  if (e.target.numericValue == null) {
                    return;
                  }
                  setRange([rangeMin, e.target.numericValue]);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <StickyFooter>
        <Tooltip text="Minimum length must be less than maximum length" isDisabled={rangeMin <= rangeMax}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MEDIUM}
            text="Generate"
            disabled={rangeMin > rangeMax}
            onClick={async () => {
              const proteinIds = currentEntityType === ModelTypeConstants.PROTEIN ? [targetId] : [];
              const moleculeIds = currentEntityType === ModelTypeConstants.MOLECULE ? [targetId] : [];
              const success = await runBoltzGen({
                minLength: rangeMin,
                maxLength: rangeMax,
                proteinIds,
                moleculeIds,
              });
              if (success) {
                navigateToList();
              }
            }}
          />
        </Tooltip>
      </StickyFooter>
    </div>
  );
}

const styles = StyleSheet.create({
  outerContainer: STICKY_FOOTER_CONTAINER_STYLES,
  content: STICKY_FOOTER_CONTENT_STYLES,
  contentPadding: {
    padding: '20px',
  },
  backButtonContainer: {
    marginBottom: '16px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  title: {
    ...FONT_PROXIMA_17,
    fontWeight: 600,
    color: GRAY_DARKER,
  },
  description: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
  },
  label: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: 600,
  },
  numericInputContainer: {
    marginTop: '24px',
    display: 'flex',
    flexDirection: 'column',
    width: '300px',
    justifyContent: 'space-between',
  },
  numericInputRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  numericInput: {
    width: '100px',
  },
});
